/**
 * ExportPreviewModal.tsx
 * Modal d'aperçu avant export avec options de format et qualité
 */
import React, { useState, useCallback } from 'react';
import { useAppStore } from '../../services/StateService';
import { useAppCoordinatorOptional } from '../../hooks/useAppCoordinator';
import { logger } from '../../utils/ProfessionalLogger';
import type { ExportFormat } from '../../types';
import { useToast } from './common/ToastProvider';

interface ExportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport?: (format: ExportFormat, options?: { dpi?: number; quality?: number }) => void;
  initialFormat?: ExportFormat;
  initialDpi?: number;
  initialQuality?: number;
}

export function ExportPreviewModal({
  isOpen,
  onClose,
  onExport,
  initialFormat = 'png',
  initialDpi,
  initialQuality = 0.95,
}: ExportPreviewModalProps): React.ReactElement | null {
  const appCoordinator = useAppCoordinatorOptional();
  const project = useAppStore(state => state.project);
  const { addToast } = useToast();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(initialFormat);
  const [dpi, setDpi] = useState(initialDpi ?? project.dpi);
  const [quality, setQuality] = useState(initialQuality);
  const [isExporting, setIsExporting] = useState(false);

  // Mettre à jour les valeurs quand les props initiales changent
  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(initialFormat);
      if (initialDpi !== undefined) setDpi(initialDpi);
      if (initialQuality !== undefined) setQuality(initialQuality);
    }
  }, [isOpen, initialFormat, initialDpi, initialQuality]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      if (onExport) {
        await onExport(selectedFormat, { dpi, quality });
      } else if (appCoordinator) {
        // P3 : exportProject() actuellement sans args dans la signature ;
        // on cast pour passer les options (le code interne gère via store).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (appCoordinator.exportProject as any)(selectedFormat, { dpi, quality });
      }
      onClose();
    } catch (error) {
      logger.error("Erreur lors de l'export:", error);
      addToast({
        type: 'error',
        message: "Erreur lors de l'export. Veuillez réessayer.",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [appCoordinator, selectedFormat, dpi, quality, onExport, onClose, addToast]);

  if (!isOpen) return null;

  const formats: Array<{ value: ExportFormat; label: string; description: string }> = [
    { value: 'png', label: 'PNG', description: 'Haute qualité, transparence' },
    { value: 'jpg', label: 'JPG', description: 'Taille réduite, pas de transparence' },
    { value: 'pdf', label: 'PDF', description: 'Impression, vectoriel' },
    { value: 'svg', label: 'SVG', description: 'Vectoriel, évolutif' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-xl)',
          maxWidth: '500px',
          width: '90%',
          boxShadow: 'var(--shadow-md)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            Exporter le projet
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 'var(--spacing-xs)',
              color: 'var(--color-text-secondary)',
            }}
            aria-label="Fermer"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
          {/* Format */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-sm)',
              }}
            >
              Format d'export
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--spacing-sm)',
              }}
            >
              {formats.map(format => (
                <button
                  key={format.value}
                  className={`btn-toggle ${selectedFormat === format.value ? 'active' : ''}`}
                  onClick={() => setSelectedFormat(format.value)}
                  style={{ textAlign: 'left', padding: 'var(--spacing-md)' }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 'var(--spacing-xs)' }}>
                    {format.label}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {format.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* DPI */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-sm)',
              }}
            >
              Résolution (DPI)
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input
                type="number"
                value={dpi}
                onChange={e => setDpi(Number(e.target.value))}
                className="input"
                style={{ flex: 1 }}
                min="72"
                max="600"
                step="1"
              />
              <span
                style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}
              >
                DPI
              </span>
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-xs)',
              }}
            >
              {selectedFormat === 'png' || selectedFormat === 'jpg'
                ? `${Math.round((project.pixelWidth / 25.4) * dpi)} × ${Math.round((project.pixelHeight / 25.4) * dpi)} px`
                : 'Vectoriel'}
            </div>
          </div>

          {/* Qualité (pour JPG) */}
          {(selectedFormat === 'jpg' || selectedFormat === 'png') && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-sm)',
                }}
              >
                Qualité: {Math.round(quality * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={quality}
                onChange={e => setQuality(Number(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-sm)',
              justifyContent: 'flex-end',
              marginTop: 'var(--spacing-md)',
            }}
          >
            <button className="btn-secondary" onClick={onClose} disabled={isExporting}>
              Annuler
            </button>
            <button className="btn-primary" onClick={handleExport} disabled={isExporting}>
              {isExporting ? 'Export en cours...' : 'Exporter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
